import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fmtMXN } from "@/lib/format";
import { MapPin, Truck, Lock, Check, Ticket, X } from "lucide-react";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coverage, setCoverage] = useState(null);
  const [zipChecking, setZipChecking] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    street: "",
    city: "",
    state: "",
    zip: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftCard, setGiftCard] = useState(null);
  const [giftErr, setGiftErr] = useState("");
  const [giftChecking, setGiftChecking] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("10:00-14:00");
  const [referralCredit, setReferralCredit] = useState(0);
  const [useReferralCredit, setUseReferralCredit] = useState(true);
  const isByop = sessionStorage.getItem("arre_byop_active") === "1" && subtotal >= 1200;
  const byopDiscount = isByop ? Math.round(subtotal * 0.15) : 0;

  // Load referral credit balance for logged-in user
  useEffect(() => {
    if (!user) return;
    api.get("/referrals/me").then((r) => setReferralCredit(r.data.credit_balance || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    try {
      const c = localStorage.getItem("arre_coverage");
      if (c) {
        const parsed = JSON.parse(c);
        if (parsed.covered && parsed.zip) {
          setCoverage(parsed);
          setForm((f) => ({ ...f, zip: parsed.zip }));
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (items.length === 0) {
      const t = setTimeout(() => navigate("/paquetes"), 200);
      return () => clearTimeout(t);
    }
  }, [items.length, navigate]);

  const checkZip = async (zip) => {
    if (!/^\d{5}$/.test(zip)) return;
    setZipChecking(true);
    try {
      const { data } = await api.get(`/coverage/check?zip=${zip}`);
      if (data.covered) {
        setCoverage(data);
        localStorage.setItem("arre_coverage", JSON.stringify(data));
        window.dispatchEvent(new Event("arre_coverage_updated"));
      } else {
        setCoverage({ covered: false, message: data.message });
      }
    } finally {
      setZipChecking(false);
    }
  };

  const applyCoupon = async () => {
    setCouponErr("");
    if (!couponCode) return;
    setCouponChecking(true);
    try {
      const baseAmount = Math.max(0, subtotal - byopDiscount);
      const { data } = await api.post(`/coupons/validate?code=${encodeURIComponent(couponCode)}&subtotal=${baseAmount}`);
      setCouponData(data);
    } catch (e) {
      setCouponData(null);
      setCouponErr(e.response?.data?.detail || "Cupón inválido");
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode("");
    setCouponErr("");
  };

  const applyGiftCard = async () => {
    setGiftErr("");
    if (!giftCode) return;
    setGiftChecking(true);
    try {
      const { data } = await api.get(`/gift-cards/validate/${encodeURIComponent(giftCode.trim())}`);
      setGiftCard(data);
    } catch (e) {
      setGiftCard(null);
      setGiftErr(e.response?.data?.detail || "Tarjeta inválida");
    } finally {
      setGiftChecking(false);
    }
  };

  const removeGiftCard = () => {
    setGiftCard(null);
    setGiftCode("");
    setGiftErr("");
  };

  const shippingFee = coverage?.covered ? coverage.shipping_fee : 0;
  const couponDiscount = couponData?.discount || 0;
  const afterDiscounts = Math.max(0, subtotal - byopDiscount - couponDiscount);
  const giftApplied = giftCard ? Math.min(giftCard.balance, afterDiscounts) : 0;
  const afterGift = Math.max(0, afterDiscounts - giftApplied);
  const referralApplied = useReferralCredit && referralCredit > 0 ? Math.min(referralCredit, afterGift) : 0;
  const totalDiscount = byopDiscount + couponDiscount + giftApplied + referralApplied;
  const total = Math.max(0, subtotal - totalDiscount + shippingFee);

  const onZipChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 5);
    setForm({ ...form, zip: v });
    if (v.length === 5) checkZip(v);
  };

  const submit = async () => {
    setErr("");
    if (!form.name || !form.email || !form.phone || !form.street || !form.city || !form.state || !form.zip) {
      setErr("Completa todos los datos de envío, compare.");
      return;
    }
    if (!coverage?.covered) {
      setErr("Tu CP no tiene cobertura todavía.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ type: i.type, id: i.id, quantity: i.quantity, variant_id: i.variant_id || null })),
        origin_url: window.location.origin,
        shipping: { ...form, delivery_date: deliveryDate, delivery_slot: deliverySlot },
        shipping_fee: shippingFee,
        user_id: user?.id || null,
        coupon_code: couponData?.code || null,
        is_byop: isByop,
        gift_card_code: giftCard?.code || null,
      };
      const { data } = await api.post("/checkout/session", payload);
      sessionStorage.setItem("arre_last_order_number", data.order_number);
      sessionStorage.removeItem("arre_byop_active");
      clear();
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al iniciar el pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="font-display text-2xl text-white/50 tracking-wider">Redirigiendo...</div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black min-h-screen py-16" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Checkout</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none mb-10">
          Casi listo,<br /><span className="text-arre-red">compare</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            <section>
              <h2 className="font-display text-2xl tracking-wider uppercase text-white mb-4">Datos de contacto</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Nombre completo" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="co-name" />
                <Input label="Correo" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="co-email" />
                <Input label="WhatsApp" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="co-phone" />
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl tracking-wider uppercase text-white mb-4">Dirección de envío</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input label="Calle y número, colonia" value={form.street} onChange={(v) => setForm({ ...form, street: v })} testid="co-street" />
                </div>
                <Input label="Ciudad" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="co-city" />
                <Input label="Estado" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="co-state" />
                <div className="md:col-span-2">
                  <Input label="Código Postal" value={form.zip} onChange={() => {}} onChangeRaw={onZipChange} testid="co-zip" />
                  {zipChecking && <p className="text-xs text-white/50 mt-2">Verificando cobertura...</p>}
                  {coverage?.covered && (
                    <div className="mt-3 p-3 border border-green-600/40 bg-green-900/10 text-sm text-white flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5" />
                      <div>
                        <div>{coverage.message}</div>
                        <div className="text-white/70 mt-1">Envío: {fmtMXN(coverage.shipping_fee)}</div>
                      </div>
                    </div>
                  )}
                  {coverage && !coverage.covered && form.zip.length === 5 && (
                    <div className="mt-3 p-3 border border-arre-red/40 bg-arre-red/10 text-sm text-white">
                      {coverage.message}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Notas al pedido (opcional)" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="co-notes" />
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl tracking-wider uppercase text-white mb-4">Fecha de entrega</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs tracking-[0.25em] uppercase text-white/60">Día de entrega (opcional)</span>
                  <input
                    type="date"
                    min={new Date(Date.now() + 24*60*60*1000).toISOString().split("T")[0]}
                    max={new Date(Date.now() + 14*24*60*60*1000).toISOString().split("T")[0]}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
                    data-testid="co-delivery-date"
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-[0.25em] uppercase text-white/60">Franja horaria</span>
                  <select
                    value={deliverySlot}
                    onChange={(e) => setDeliverySlot(e.target.value)}
                    className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
                    data-testid="co-delivery-slot"
                  >
                    <option value="10:00-14:00">Mañana (10:00 - 14:00)</option>
                    <option value="14:00-18:00">Tarde (14:00 - 18:00)</option>
                    <option value="18:00-21:00">Noche (18:00 - 21:00)</option>
                    <option value="cualquier-hora">Cualquier hora</option>
                  </select>
                </label>
              </div>
              <p className="text-[11px] text-white/40 mt-2">
                Si no eliges fecha, te enviamos lo más pronto posible. CDMX y EDOMEX: mismo día si pides antes de las 12.
              </p>
            </section>

            {err && (
              <div className="p-3 border border-arre-red bg-arre-red/10 text-sm text-white" data-testid="checkout-error">
                {err}
              </div>
            )}

            <div className="flex items-center gap-3 text-xs text-white/50">
              <Lock className="w-4 h-4" />
              <span>Pago 100% seguro procesado por Stripe — Tarjeta / débito.</span>
            </div>
          </div>

          {/* Summary */}
          <aside className="bg-arre-surface border border-arre-border p-6 h-fit sticky top-28">
            <h3 className="font-display text-2xl tracking-wider uppercase text-white mb-5">Resumen</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 mb-5 border-b border-arre-border pb-5">
              {items.map((it) => (
                <div key={`${it.type}-${it.id}`} className="flex gap-3 items-start">
                  <div
                    className="w-14 h-14 bg-arre-black bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${it.image})` }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base tracking-wider uppercase text-white leading-tight">{it.name}</p>
                    <p className="text-xs text-white/50">x{it.quantity}</p>
                  </div>
                  <div className="text-white font-display text-sm">{fmtMXN(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm">
              <Row label="Subtotal" value={fmtMXN(subtotal)} />
              {isByop && byopDiscount > 0 && (
                <div className="flex items-center justify-between text-arre-red font-bold" data-testid="byop-discount">
                  <span>Combo BYOP 15% OFF</span>
                  <span>-{fmtMXN(byopDiscount)}</span>
                </div>
              )}
              {couponData && (
                <div className="flex items-center justify-between text-arre-red font-bold" data-testid="coupon-applied">
                  <span className="flex items-center gap-1">
                    <Ticket className="w-3.5 h-3.5" /> {couponData.code}
                    <button onClick={removeCoupon} className="text-white/40 hover:text-white ml-1"><X className="w-3 h-3" /></button>
                  </span>
                  <span>-{fmtMXN(couponDiscount)}</span>
                </div>
              )}
              {giftCard && giftApplied > 0 && (
                <div className="flex items-center justify-between text-arre-red font-bold" data-testid="gift-applied">
                  <span className="flex items-center gap-1">
                    🎁 {giftCard.code}
                    <button onClick={removeGiftCard} className="text-white/40 hover:text-white ml-1"><X className="w-3 h-3" /></button>
                  </span>
                  <span>-{fmtMXN(giftApplied)}</span>
                </div>
              )}
              {user && referralCredit > 0 && (
                <label className="flex items-center justify-between cursor-pointer" data-testid="referral-credit-toggle">
                  <span className="flex items-center gap-2 text-white/80">
                    <input
                      type="checkbox"
                      checked={useReferralCredit}
                      onChange={(e) => setUseReferralCredit(e.target.checked)}
                      className="accent-arre-red"
                      data-testid="use-referral-credit"
                    />
                    Saldo Arre ({fmtMXN(referralCredit)})
                  </span>
                  {referralApplied > 0 && (
                    <span className="text-arre-red font-bold">-{fmtMXN(referralApplied)}</span>
                  )}
                </label>
              )}
              <Row label="Envío" value={shippingFee ? fmtMXN(shippingFee) : <span className="text-white/40">Calcula tu CP</span>} />
              {coverage?.same_day && (
                <div className="flex items-center gap-2 text-xs text-arre-red font-bold tracking-widest uppercase">
                  <Truck className="w-4 h-4" /> Entrega hoy mismo
                </div>
              )}
            </div>

            {/* Coupon input */}
            {!couponData && (
              <div className="mt-4 pt-4 border-t border-arre-border" data-testid="coupon-input-wrap">
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 flex items-center gap-1">
                  <Ticket className="w-3 h-3" /> ¿Tienes un cupón?
                </div>
                <div className="flex gap-2">
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Código"
                    className="flex-1 bg-arre-black border border-arre-border px-3 py-2 text-white text-sm font-display tracking-wider focus:outline-none focus:border-arre-red"
                    data-testid="coupon-input"
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={!couponCode || couponChecking}
                    className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xs tracking-widest uppercase px-4 py-2 disabled:opacity-50"
                    data-testid="apply-coupon-btn"
                  >
                    {couponChecking ? "..." : "Aplicar"}
                  </button>
                </div>
                {couponErr && <p className="text-arre-red text-xs mt-2" data-testid="coupon-error">{couponErr}</p>}
              </div>
            )}

            {/* Gift card input */}
            {!giftCard && (
              <div className="mt-3" data-testid="gift-input-wrap">
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">🎁 ¿Tienes tarjeta de regalo?</div>
                <div className="flex gap-2">
                  <input
                    value={giftCode}
                    onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                    placeholder="GIFT-XXXXXXXXXX"
                    className="flex-1 bg-arre-black border border-arre-border px-3 py-2 text-white text-sm font-display tracking-wider focus:outline-none focus:border-arre-red"
                    data-testid="gift-input"
                  />
                  <button
                    onClick={applyGiftCard}
                    disabled={!giftCode || giftChecking}
                    className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xs tracking-widest uppercase px-4 py-2 disabled:opacity-50"
                    data-testid="apply-gift-btn"
                  >
                    {giftChecking ? "..." : "Aplicar"}
                  </button>
                </div>
                {giftErr && <p className="text-arre-red text-xs mt-2" data-testid="gift-error">{giftErr}</p>}
              </div>
            )}
            {giftCard && (
              <div className="mt-3 text-[11px] text-white/50" data-testid="gift-balance-info">
                Saldo de la tarjeta: {fmtMXN(giftCard.balance)} · se aplicará {fmtMXN(giftApplied)} en esta compra
              </div>
            )}
            <div className="flex items-center justify-between mt-5 pt-5 border-t border-arre-border">
              <span className="font-display text-xl uppercase tracking-wider text-white">Total</span>
              <span className="font-display text-4xl text-white" data-testid="checkout-total">{fmtMXN(total)}</span>
            </div>
            <button
              onClick={submit}
              disabled={loading || !coverage?.covered}
              className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase py-4 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="place-order-btn"
            >
              {loading ? "Procesando..." : "Pagar con Stripe →"}
            </button>
            <p className="text-[11px] text-white/40 mt-3 text-center">Serás redirigido al pago seguro.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, onChangeRaw, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.25em] uppercase text-white/60">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChangeRaw ? onChangeRaw : (e) => onChange(e.target.value)}
        className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
        data-testid={testid}
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-white/70">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
